/**
 * error_shown: every error a person actually SAW, with the text they saw.
 *
 * Two ways in. Surfaces that know they are showing an error (showError, the
 * execution ErrorCard) call reportShownError() directly. Everything else is
 * caught by watchRenderedErrors(): one MutationObserver for elements with
 * role="alert" (the shared Alert's error tone, and the ~150 local `setError`
 * states that render through it) or a data-testid ending in `-error`, the
 * convention inline field and form errors follow (`secrets-name-error`). That
 * covers errors that never call showError without wiring each of them.
 *
 * The text is scrubbed (scrubErrorText) before it leaves: an error can echo a
 * typed value, a record id or a customer's email, and those are replaced while
 * the sentence stays readable.
 */

import { scrubErrorText, type FailureCategory } from "@fastn/core";
import { BASE_PATH } from "./utils";
import { capture } from "./posthog";
import { recentFailedRequest, routePattern } from "./request-failures";

/**
 * What each browser says when fetch got no response at all: the request never
 * reached the api, or its answer never came back. None of it means anything to
 * the person reading it. Matched as a whole line, since a page can render it
 * under its own heading ("Could not load your keys" / "Failed to fetch"), while
 * our own "Failed to fetch scopes" is not a network failure.
 */
const NETWORK_FAILURE = new Set([
  "failed to fetch", // Chromium
  "typeerror: failed to fetch",
  "networkerror when attempting to fetch resource.", // Firefox
  "load failed", // Safari
  "network request failed",
]);

export function isNetworkFailure(message: unknown): boolean {
  return typeof message === "string" && message.split("\n").some((line) => NETWORK_FAILURE.has(line.trim().toLowerCase()));
}

/** What a person is shown instead of the browser's wording. */
export const NETWORK_FAILURE_HEADING = "Couldn't reach fastn";
export const NETWORK_FAILURE_MESSAGE = "No response came back from fastn. Check your connection, then try again.";

/** The same error on the same page is one sighting, however often it re-renders. */
const DEDUPE_MS = 30_000;
const recent = new Map<string, number>();
/** When any error was last put on screen, deduped or not. */
let lastShownAt = 0;

/** Whether some error has been shown on screen since `t`. */
export function errorShownSince(t: number): boolean {
  return lastShownAt >= t;
}

/** The current page as a route pattern: base path stripped, ids as `:id`. */
export function currentRoute(): string {
  const pathname = window.location.pathname;
  const path = BASE_PATH && pathname.startsWith(BASE_PATH) ? pathname.slice(BASE_PATH.length) || "/" : pathname;
  return routePattern(path).replace(/^\/api\/v\d+/, "") || "/";
}

export interface ShownError {
  kind: "error_alert" | "error_card" | "toast" | "inline_alert" | "unhandled_failure";
  message: unknown;
  heading?: unknown;
  failureCategory?: FailureCategory;
  errorCode?: string | null;
  httpStatus?: number | null;
  entity?: string | null;
  isRecoverable?: boolean;
}

export function reportShownError(e: ShownError): void {
  lastShownAt = Date.now();
  try {
    const route = currentRoute();
    const message = scrubErrorText(e.message);
    const heading = scrubErrorText(e.heading);
    // The request that failed just before, as a hint only: it may be an
    // unrelated background read, so its status and category are never taken.
    const failed = e.httpStatus == null ? recentFailedRequest() : null;
    const category = e.failureCategory ?? (isNetworkFailure(e.message) ? "network" : "unknown");
    const key = `${e.kind}|${route}|${heading ?? ""}|${message ?? ""}`;
    const now = Date.now();
    const last = recent.get(key);
    if (last !== undefined && now - last < DEDUPE_MS) return;
    recent.set(key, now);
    if (recent.size > 200) recent.delete(recent.keys().next().value as string);
    capture("error_shown", {
      failure_category: category,
      error_code: e.errorCode ?? null,
      http_status: e.httpStatus ?? null,
      entity: e.entity ?? (route.split("/").filter(Boolean)[0] ?? "home"),
      is_recoverable: e.isRecoverable ?? true,
      message,
      heading,
      kind: e.kind,
      route,
      failed_request: failed?.request ?? null,
      failed_request_status: failed?.status ?? null,
    });
  } catch {
    // Telemetry must never break the page showing the error.
  }
}

/**
 * Most inline errors are not marked up as errors at all: about 85 components
 * draw one as red text (`text-destructive`, `text-error`, `--fgColor-danger`).
 * The same classes also colour "Danger zone" headings, delete buttons and the
 * required-field asterisk, so red text counts as an error a person saw only
 * when it appears within FAILURE_WINDOW_MS of a failed request or action
 * (noteFailure), and only when it reads as a message: not inside a control or
 * a heading, and longer than a word.
 */
const STYLED_ERROR = '.text-destructive, .text-error, .text-danger, [style*="fgColor-danger"]';
const FAILURE_WINDOW_MS = 5_000;
let lastFailureAt = 0;
/** When red text last appeared outside a control or heading, any length. */
let lastStyledAt = 0;
/** The page a failure happened on, so the fallback stays quiet once the person has moved on. */
let failureRoute: string | null = null;

/**
 * An ACTION just failed (a create, update or delete, or a mutation): red text
 * appearing now is its error. A failed read does not open the window: a page
 * polling a broken endpoint would hold it open, and every red paragraph that
 * then opened ("This cannot be undone") would count as an error.
 */
export function noteFailure(now: number = Date.now()): void {
  lastFailureAt = now;
  failureRoute = typeof window === "undefined" ? null : currentRoute();
}

function readsAsMessage(el: Element): boolean {
  if (el.closest("button, a, label, summary, h1, h2, h3, h4, h5, h6, [role='button'], [role='menuitem'], [role='tab']")) return false;
  const text = ((el as HTMLElement).innerText ?? el.textContent ?? "").trim();
  return text.length >= 8 && /\s/.test(text);
}

/**
 * Whether the page is showing this failure already, in any form: an error
 * reported since `since`, or the failure's own text somewhere on screen. The
 * fallback (unhandled-failures.ts) shows nothing when it is.
 */
export function failureVisible(message: string, since: number): boolean {
  if (errorShownSince(since)) return true;
  // Some red text appeared after the failure, even a short "Required", or the
  // person is on another page now: either way the fallback would be noise.
  if (lastStyledAt >= since) return true;
  if (failureRoute !== null && typeof window !== "undefined" && currentRoute() !== failureRoute) return true;
  if (typeof document === "undefined") return false;
  const probe = message.trim().slice(0, 60);
  if (probe.length < 8) return false;
  const shown = (document.body?.innerText ?? document.body?.textContent ?? "").includes(probe);
  if (shown) reportShownError({ kind: "inline_alert", message });
  return shown;
}

/** Already reported by their own surface; observing them too would double count. */
const OWN_SURFACES = '[data-testid="error-region"], [data-error-reported]';

const ERROR_ELEMENTS = '[role="alert"], [data-testid$="-error"], [data-testid$="-error-message"]';

/**
 * A field's own message ("Enter a valid domain"), not a failure: shown beside a
 * field marked aria-invalid, with no request or action failing just before it.
 * Reported as `validation`, the person's own input, so the onboarding error
 * matrix does not read it as an unexplained error and on-call is not paged for
 * a typo (dev, 23 Oct 2026: it arrived as `unknown`, which pages).
 */
function isFieldValidation(el: Element): boolean {
  if (Date.now() - lastFailureAt <= FAILURE_WINDOW_MS) return false;
  let scope = el.parentElement;
  for (let i = 0; i < 3 && scope; i++, scope = scope.parentElement) {
    if (scope.querySelector('[aria-invalid="true"]')) return true;
  }
  return false;
}

function reportAlertElement(el: Element): void {
  if (el.closest(OWN_SURFACES)) return;
  // Inside another error element: that one is the error, counted once.
  if (el.parentElement?.closest(ERROR_ELEMENTS)) return;
  const text = (el as HTMLElement).innerText ?? el.textContent ?? "";
  if (!text.trim()) return;
  reportShownError({
    kind: "inline_alert",
    message: text,
    ...(isFieldValidation(el) ? { failureCategory: "validation" as const, isRecoverable: true } : {}),
  });
}

const CONTROL_OR_HEADING = "button, a, label, summary, h1, h2, h3, h4, h5, h6, [role='button'], [role='menuitem'], [role='tab']";

function reportStyledElement(el: Element): void {
  if (el.closest(OWN_SURFACES) || el.closest(CONTROL_OR_HEADING)) return;
  if (((el as HTMLElement).innerText ?? el.textContent ?? "").trim()) lastStyledAt = Date.now();
  if (Date.now() - lastFailureAt > FAILURE_WINDOW_MS) return;
  if (el.closest(ERROR_ELEMENTS) || !readsAsMessage(el)) return;
  reportAlertElement(el);
}

/**
 * The page's own element for a node a MutationObserver handed over (or for its
 * parent, when `viaParent`), or null. Text filled into an element that was
 * already there arrives as a text node, so that resolves to its parent.
 *
 * Firefox can also hand over a node page script may not read (a
 * cross-compartment wrapper, e.g. content an extension injected or one adopted
 * from a cross-origin frame): every property access on it throws "Permission
 * denied to access property", which escaped the observer as an uncaught page
 * error (KAT-8734). Such a node is not the page's own UI, so it is skipped.
 * Only resolving the node is guarded: once it is a same-realm Element, the
 * reporting below runs unguarded so a real bug there still surfaces.
 */
function ownElement(input: Node, viaParent = false): Element | null {
  try {
    const node = viaParent || input.nodeType === 3 ? input.parentElement : input;
    return node instanceof Element ? node : null;
  } catch {
    return null;
  }
}

function scan(input: Node): void {
  const node = ownElement(input);
  if (!node) return;
  if (node.matches(ERROR_ELEMENTS)) reportAlertElement(node);
  node.querySelectorAll(ERROR_ELEMENTS).forEach(reportAlertElement);
  if (node.matches(STYLED_ERROR)) reportStyledElement(node);
  node.querySelectorAll(STYLED_ERROR).forEach(reportStyledElement);
}

let observer: MutationObserver | null = null;

/** Start watching the page for rendered error alerts. Idempotent. */
export function watchRenderedErrors(root: Node = document.body): () => void {
  if (observer || typeof MutationObserver === "undefined") return () => undefined;
  observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === "childList") m.addedNodes.forEach(scan);
      else if (m.type === "characterData") {
        const parent = ownElement(m.target, true);
        const el = parent?.closest(ERROR_ELEMENTS);
        if (el) reportAlertElement(el);
        else {
          const styled = parent?.closest(STYLED_ERROR);
          if (styled) reportStyledElement(styled);
        }
      }
    }
  });
  observer.observe(root, { childList: true, subtree: true, characterData: true });
  scan(root);
  return () => {
    observer?.disconnect();
    observer = null;
  };
}

/** Test seam. */
export function __resetErrorAnalyticsForTests(): void {
  recent.clear();
  lastShownAt = 0;
  lastFailureAt = 0;
  lastStyledAt = 0;
  failureRoute = null;
  observer?.disconnect();
  observer = null;
}
